import { useState, useEffect, useContext, useMemo } from 'react';
import { Box, Paper, Typography } from '@material-ui/core';

import { UserFormAnswer, FormDoc, User } from '@vyce/core/src/types';
import { DeviceContext } from '@vyce/core/src/contexts';
import { getUserFormAnswersByAdminRequest } from '@vyce/core/src/api/forms';
import dayjs from 'dayjs';

import { FormSummaryToolbar } from './components/FormSummaryToolbar';
import { FormSummaryPoweredByScreen } from './components/FormSummaryPoweredBy';
import { FormPrintLayout } from './components/FormPrintLayout';
import { AutocompleteUser } from './components/FormSearchParticipants';
import { FormSearchParticipants } from './components/FormSearchParticipants';
import { FormToAnswer } from './components/FormToAnswer';
import { PageLoading } from '@vyce/core/src/components/PageLoading';
import { getParticipantDisplayName } from './utils/formParticipantDisplay';
import { printFormAsPdf } from './utils/formPrintExport';

export const FormResultByUser: React.FC<{
  form: FormDoc | null;
  user: User;
  companyId: string;
  companyLogoUrl?: string | null;
}> = ({ form, user, companyId, companyLogoUrl }) => {
  const { isMobile } = useContext(DeviceContext);
  const [selectedUser, setSelectedUser] = useState<AutocompleteUser | null>(null);
  const [userFormAnswers, setUserFormAnswers] = useState<UserFormAnswer[] | null>([]);
  const [loading, setLoading] = useState(false);

  const completedAt = useMemo(() => {
    return userFormAnswers?.[0]?.completedAt ? dayjs(userFormAnswers[0].completedAt._seconds * 1000) : null;
  }, [userFormAnswers]);

  const hasAnswersToShow = Boolean(
    form && selectedUser && userFormAnswers && userFormAnswers.length > 0 && !loading
  );

  const getUserFormAnswersByAdmin = async (targetUserId: string) => {
    try {
      setLoading(true);
      const response = await getUserFormAnswersByAdminRequest({
        companyId,
        formId: form?.id || '',
        adminId: user.uuid,
        targetUserId,
      });
      setUserFormAnswers(response.answers);
      setLoading(false);
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedUser?.uid) {
      getUserFormAnswersByAdmin(selectedUser.uid);
    } else {
      setUserFormAnswers(null);
    }
  }, [selectedUser?.uid]);

  return (
    <FormPrintLayout
      companyLogoUrl={companyLogoUrl}
      toolbar={
        hasAnswersToShow ? (
          <FormSummaryToolbar isMobile={isMobile} onExportPdf={printFormAsPdf} />
        ) : undefined
      }
    >
      <Paper
        className="form-summary-print-user-shell"
        variant="outlined"
        style={{ padding: 24, width: '100%' }}
      >
        <Box className="no-print">
          <FormSearchParticipants
            selectedUser={selectedUser}
            setSelectedUser={setSelectedUser}
            label="Select a user..."
            formId={form?.id || ''}
            companyId={companyId}
            userId={user?.uuid || ''}
          />
        </Box>

        {selectedUser && (
          <Typography
            variant="body1"
            color="textSecondary"
            component="p"
            style={{ marginTop: 16, marginBottom: 0 }}
          >
            Respondent: <strong>{getParticipantDisplayName(selectedUser)}</strong>
          </Typography>
        )}

        {completedAt && (
          <Typography
            variant="body1"
            color="textSecondary"
            component="p"
            style={{ marginTop: 8, marginBottom: 0 }}
          >
            Form completed on: {completedAt.format('DD/MM/YYYY HH:mm')}
          </Typography>
        )}

        {selectedUser && !loading && !userFormAnswers && (
          <Paper variant="outlined" style={{ padding: 24, width: '100%', marginTop: 16 }}>
            <Typography variant="body1" color="textSecondary">
              No answers found for this user
            </Typography>
          </Paper>
        )}
      </Paper>

      {loading && (
        <Box className="no-print" height={200} display="flex" alignItems="center" justifyContent="center">
          <PageLoading />
        </Box>
      )}

      {!loading && form && userFormAnswers && userFormAnswers.length > 0 && (
        <FormToAnswer embedded form={form} userAnswer={userFormAnswers[0]} user={user} />
      )}

      <FormSummaryPoweredByScreen />
    </FormPrintLayout>
  );
};
